import type { Space } from '@cocoonspace/sdk-js/types/space'
import type { PolymorphicElement } from '@cocoonspace/types/shared'
import { type LucideProps, MapPin } from 'lucide-react'
import { useSpaceAddress } from '~/domains/spaces/hooks/use-space-address.hook'
import { cn } from '~/lib/utils'

interface SpaceFullAddressBaseProps
	extends React.DetailedHTMLProps<
		React.HTMLAttributes<HTMLDivElement>,
		HTMLDivElement
	> {
	space: Pick<Space, 'address'>
	dense?: boolean
	showIcon?: boolean
	iconProps?: LucideProps
}

export type SpaceFullAddressProps<T extends React.ElementType> =
	PolymorphicElement<T, SpaceFullAddressBaseProps>

export const SpaceFullAddress = <T extends React.ElementType>({
	space,
	dense,
	showIcon = true,
	iconProps,
	as,
	...props
}: SpaceFullAddressProps<T>) => {
	const Tag = as || 'div'
	const { fullAddress } = useSpaceAddress(space, dense)

	return (
		<Tag
			data-testid='space-full-address'
			{...props}
			className={cn('flex flex-row items-center truncate', props.className)}
		>
			{showIcon && (
				<MapPin
					size={16}
					strokeWidth={1.5}
					{...iconProps}
					className={cn('mr-1 shrink-0', iconProps?.className)}
				/>
			)}

			<span className='truncate'>{fullAddress}</span>
		</Tag>
	)
}
